import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Stack,
    Box,
    Alert,
    InputAdornment,
    IconButton,
} from '@mui/material';
import { Eye, EyeOff, CheckCircle } from 'lucide-react';
import { useResetPassword } from '../../../hooks/useAuth';
import { useToastContext } from '../../../context/ToastContext';

export default function ResetPassword() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const navigate = useNavigate();
    const { addToast } = useToastContext();
    const resetPasswordMutation = useResetPassword();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!token) {
            addToast('Invalid or missing reset token', 'error');
            return;
        }

        if (newPassword.length < 8) {
            addToast('Password must be at least 8 characters', 'error');
            return;
        }

        if (newPassword !== confirmPassword) {
            addToast('Passwords do not match', 'error');
            return;
        }

        try {
            await resetPasswordMutation.mutateAsync({ token, newPassword });
            setShowSuccess(true);
            addToast('Password reset successfully!', 'success');
        } catch (error: any) {
            console.error('Reset password error:', error);
            const errorMessage = error.response?.data?.message || 'Failed to reset password. The link may have expired.';
            addToast(errorMessage, 'error');
        }
    };

    if (!token) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="80vh"
                px={2}
            >
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <Card sx={{ maxWidth: 500, width: '100%' }}>
                        <CardContent>
                            <Typography variant="h5" gutterBottom align="center">
                                Invalid Reset Link
                            </Typography>
                            
                            <Alert severity="error" sx={{ mt: 2, mb: 3 }}>
                                This password reset link is invalid or has expired.
                            </Alert>

                            <Button
                                variant="contained"
                                fullWidth
                                onClick={() => navigate('/forgot-password')}
                            >
                                Request New Link
                            </Button>
                        </CardContent>
                    </Card>
                </motion.div>
            </Box>
        );
    }

    if (showSuccess) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="80vh"
                px={2}
            >
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <Card sx={{ maxWidth: 500, width: '100%' }}>
                        <CardContent>
                            <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                                <CheckCircle size={64} color="#4caf50" />
                                <Typography variant="h5" align="center">
                                    Password Reset Successful
                                </Typography>
                            </Box>
                            
                            <Alert severity="success" sx={{ mt: 3, mb: 2 }}>
                                Your password has been reset successfully. You can now log in with your new password.
                            </Alert>

                            <Button
                                variant="contained"
                                fullWidth
                                onClick={() => navigate('/')}
                            >
                                Go to Login
                            </Button>
                        </CardContent>
                    </Card>
                </motion.div>
            </Box>
        );
    }

    return (
        <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="80vh"
            px={2}
        >
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Card sx={{ maxWidth: 500, width: '100%' }}>
                    <CardContent>
                        <Typography variant="h5" gutterBottom align="center">
                            Reset Your Password
                        </Typography>
                        
                        <Typography variant="body2" color="text.secondary" paragraph align="center">
                            Enter your new password below.
                        </Typography>

                        <form onSubmit={handleSubmit}>
                            <Stack spacing={2} mt={3}>
                                <TextField
                                    type={showPassword ? 'text' : 'password'}
                                    label="New Password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    fullWidth
                                    required
                                    autoFocus
                                    helperText="Must be at least 8 characters"
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    edge="end"
                                                >
                                                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <TextField
                                    type={showPassword ? 'text' : 'password'}
                                    label="Confirm Password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    fullWidth
                                    required
                                    error={confirmPassword.length > 0 && newPassword !== confirmPassword}
                                    helperText={
                                        confirmPassword.length > 0 && newPassword !== confirmPassword
                                            ? 'Passwords do not match'
                                            : ''
                                    }
                                />

                                <Button
                                    type="submit"
                                    variant="contained"
                                    fullWidth
                                    disabled={resetPasswordMutation.isPending}
                                >
                                    {resetPasswordMutation.isPending
                                        ? 'Resetting...'
                                        : 'Reset Password'}
                                </Button>
                            </Stack>
                        </form>
                    </CardContent>
                </Card>
            </motion.div>
        </Box>
    );
}